import { useRef, useState } from "react";
import {
  AlertCircle,
  ArrowRight,
  FileText,
  Loader2,
  Pencil,
  Plus,
  Upload,
  X,
} from "lucide-react";
import {
  BankingContractPayload,
  BankingContractPeriodPayload,
  extractBankingContractPdf,
  getBankingExtractStatus,
  saveBankingContract,
} from "../../../../Rest_api/RestApi";
import { TraderSelect } from "./TraderSelect";

export type MaxQuantumMode = "exact" | "max";
export type BasisMode = "firm" | "asAndWhen" | "nonFirm";

export type FormSupplyPeriod = {
  start: string;
  end: string;
  quantum: string;
  maxQuantum: MaxQuantumMode;
  fromTime: string;
  toTime: string;
  basis: BasisMode;
  basisRaw: string;
};

export type FormReturnPeriod = {
  start: string;
  end: string;
  bankingFactor: string;
  quantum: string;
  maxQuantum: MaxQuantumMode;
  fromTime: string;
  toTime: string;
  basis: BasisMode;
  basisRaw: string;
};

export type NewContractInput = {
  loaNumber: string;
  loaDate: string;
  supplier: string;
  receiver: string;
  notes: string;
  traderName: string;
  supplyPeriods: FormSupplyPeriod[];
  returnPeriods: FormReturnPeriod[];
};

type Step = "choose" | "manual" | "upload";

const EMPTY_SUPPLY: FormSupplyPeriod = {
  start: "",
  end: "",
  quantum: "",
  maxQuantum: "exact",
  fromTime: "",
  toTime: "",
  basis: "asAndWhen",
  basisRaw: "",
};

const EMPTY_RETURN: FormReturnPeriod = {
  start: "",
  end: "",
  bankingFactor: "",
  quantum: "",
  maxQuantum: "exact",
  fromTime: "",
  toTime: "",
  basis: "asAndWhen",
  basisRaw: "",
};

const EMPTY_FORM: NewContractInput = {
  loaNumber: "",
  loaDate: "",
  supplier: "",
  receiver: "",
  notes: "",
  traderName: "",
  supplyPeriods: [{ ...EMPTY_SUPPLY }],
  returnPeriods: [{ ...EMPTY_RETURN }],
};

const normalizeTime = (raw: any): string => {
  const s = `${raw ?? ""}`.trim();
  if (!s) return "";
  const m = s.match(/(\d{1,2})\s*[:.\sh]\s*(\d{1,2})/i);
  if (!m) return "";
  let h = Number(m[1]);
  const mm = Number(m[2]);
  if (!Number.isFinite(h) || !Number.isFinite(mm)) return "";
  if (mm < 0 || mm > 59) return "";
  if (h === 24 && mm === 0) return "23:59";
  if (h < 0 || h > 24) return "";
  h = Math.min(23, h);
  return `${h.toString().padStart(2, "0")}:${mm.toString().padStart(2, "0")}`;
};

const pickTime = (...candidates: any[]): string => {
  for (const c of candidates) {
    const v = normalizeTime(c);
    if (v) return v;
  }
  return "";
};

const normalizeBasis = (raw: any): BasisMode => {
  const v = `${raw ?? ""}`.toLowerCase();
  // if (v.includes("non")) return "nonFirm";
  if (v.includes("firm")) return "firm";
  return "asAndWhen";
};

export function AddContractModal({
  open,
  onClose,
  onSubmit,
}: {
  open: boolean;
  onClose: () => void;
  onSubmit: (c: NewContractInput, savedId?: string) => void;
}) {
  const [step, setStep] = useState<Step>("choose");
  const [form, setForm] = useState<NewContractInput>(EMPTY_FORM);
  const [pdfFile, setPdfFile] = useState<File | null>(null);
  const [pdfMeta, setPdfMeta] = useState<{
    s3Key: string;
    filename: string;
  } | null>(null);
  const [uploading, setUploading] = useState(false);
  const [uploadError, setUploadError] = useState<string | null>(null);
  const [saving, setSaving] = useState(false);
  const [saveError, setSaveError] = useState<string | null>(null);
  const [traderMissing, setTraderMissing] = useState(false);
  const fileInputRef = useRef<HTMLInputElement | null>(null);
  const cancelPollRef = useRef(false);

  // useEffect(() => {
  //   if (!saveError) return;
  //   const t = setTimeout(() => setSaveError(null), 5000);
  //   return () => clearTimeout(t);
  // }, [saveError]);

  if (!open) return null;

  const resetAndClose = () => {
    cancelPollRef.current = true;
    setStep("choose");
    setForm(EMPTY_FORM);
    setPdfFile(null);
    setPdfMeta(null);
    setUploadError(null);
    setUploading(false);
    setSaving(false);
    setSaveError(null);
    setTraderMissing(false);
    onClose();
  };

  const setField = <K extends keyof NewContractInput>(
    key: K,
    value: NewContractInput[K],
  ) => setForm((f) => ({ ...f, [key]: value }));

  const updateSupply = <K extends keyof FormSupplyPeriod>(
    idx: number,
    key: K,
    value: FormSupplyPeriod[K],
  ) =>
    setForm((f) => ({
      ...f,
      supplyPeriods: f.supplyPeriods.map((p, i) =>
        i === idx ? { ...p, [key]: value } : p,
      ),
    }));

  const updateReturn = <K extends keyof FormReturnPeriod>(
    idx: number,
    key: K,
    value: FormReturnPeriod[K],
  ) =>
    setForm((f) => ({
      ...f,
      returnPeriods: f.returnPeriods.map((p, i) =>
        i === idx ? { ...p, [key]: value } : p,
      ),
    }));

  const addSupply = () =>
    setForm((f) => ({
      ...f,
      supplyPeriods: [...f.supplyPeriods, { ...EMPTY_SUPPLY }],
    }));
  const removeSupply = (idx: number) =>
    setForm((f) => ({
      ...f,
      supplyPeriods:
        f.supplyPeriods.length === 1
          ? f.supplyPeriods
          : f.supplyPeriods.filter((_, i) => i !== idx),
    }));

  const addReturn = () =>
    setForm((f) => ({
      ...f,
      returnPeriods: [...f.returnPeriods, { ...EMPTY_RETURN }],
    }));
  const removeReturn = (idx: number) =>
    setForm((f) => ({
      ...f,
      returnPeriods:
        f.returnPeriods.length === 1
          ? f.returnPeriods
          : f.returnPeriods.filter((_, i) => i !== idx),
    }));

  const basisToApi = (b: BasisMode): string =>
    b === "asAndWhen" ? "as_and_when" : b === "nonFirm" ? "non_firm" : "firm";

  const parseQuantum = (q: string): number | null => {
    const trimmed = (q ?? "").toString().trim();
    if (!trimmed) return null;
    const n = Number(trimmed.replace(/[^0-9.\-]/g, ""));
    return Number.isFinite(n) ? n : null;
  };

  const parseBankingFactor = (bf: string): number | undefined => {
    const trimmed = (bf ?? "").toString().trim();
    if (!trimmed) return undefined;
    const n = Number(trimmed.replace(/[^0-9.\-]/g, ""));
    return Number.isFinite(n) ? n : undefined;
  };

  const buildPayload = (): BankingContractPayload => {
    const supply_periods: BankingContractPeriodPayload[] =
      form.supplyPeriods.map((p) => ({
        start: p.start,
        end: p.end,
        quantum_mw: parseQuantum(p.quantum),
        quantum_is_max: p.maxQuantum === "max",
        from_time: p.fromTime,
        to_time: p.toTime,
        basis: basisToApi(p.basis),
        basis_raw: p.basisRaw,
      }));

    const return_periods: BankingContractPeriodPayload[] =
      form.returnPeriods.map((p) => {
        const bf = parseBankingFactor(p.bankingFactor);
        const row: BankingContractPeriodPayload = {
          start: p.start,
          end: p.end,
          quantum_mw: parseQuantum(p.quantum),
          quantum_is_max: p.maxQuantum === "max",
          from_time: p.fromTime,
          to_time: p.toTime,
          basis: basisToApi(p.basis),
          basis_raw: p.basisRaw,
        };
        if (bf !== undefined) row.banking_factor_pct = bf;
        return row;
      });

    const payload: BankingContractPayload = {
      loa_number: form.loaNumber,
      loa_date: form.loaDate,
      supplier: form.supplier,
      receiver: form.receiver,
      notes: form.notes,
      trader: form.traderName,
      supply_periods,
      return_periods,
    };

    // Echo the PDF reference back to the API for upload-based contracts.
    if (pdfMeta?.s3Key) {
      payload.pdf_s3_key = pdfMeta.s3Key;
      if (pdfMeta.filename) payload.pdf_filename = pdfMeta.filename;
    }

    return payload;
  };

  const extractBackendError = (res: any): string => {
    const data = res?.data;
    if (!data) return res?.message || "Failed to save contract";
    if (typeof data === "string") return data;
    return (
      data.error ||
      data.message ||
      data.detail ||
      (Array.isArray(data.errors) ? data.errors.join(", ") : null) ||
      res.message ||
      "Failed to save contract"
    );
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (saving) return;
    if (!form.supplier.trim() || !form.receiver.trim()) return;
    if (!form.traderName.trim()) {
      setTraderMissing(true);
      return;
    }
    setSaving(true);
    setSaveError(null);
    try {
      const res = await saveBankingContract(buildPayload());
      if (res && res.success === false) {
        setSaveError(extractBackendError(res));
        return;
      }
      const savedId: string | undefined = res?.id ?? res?.data?.id ?? undefined;
      onSubmit(form, savedId);
      resetAndClose();
    } catch (err: any) {
      setSaveError(
        typeof err === "string" ? err : err?.message || "Failed to save contract",
      );
    } finally {
      setSaving(false);
    }
  };

  const applyExtracted = (data: any) => {
    const src = data?.data ?? data ?? {};

    const s3Key = src.pdf_s3_key ?? data?.pdf_s3_key ?? "";
    const filename =
      src.pdf_filename ?? data?.pdf_filename ?? pdfFile?.name ?? "";
    setPdfMeta(s3Key ? { s3Key, filename } : null);

    const supply = Array.isArray(src.supply_periods) ? src.supply_periods : [];
    const ret = Array.isArray(src.return_periods) ? src.return_periods : [];

    const mappedSupply: FormSupplyPeriod[] = supply.map((p: any) => ({
      start: p?.start ?? "",
      end: p?.end ?? "",
      quantum: p?.quantum_mw == null ? "" : `${p.quantum_mw}`,
      maxQuantum: p?.quantum_is_max ? "max" : "exact",
      fromTime: pickTime(p?.from_time, p?.fromTime, p?.start_time, p?.startTime),
      toTime: pickTime(p?.to_time, p?.toTime, p?.end_time, p?.endTime),
      basis: normalizeBasis(p?.basis),
      basisRaw: p?.basis_raw ?? "",
    }));

    const mappedReturn: FormReturnPeriod[] = ret.map((p: any) => ({
      start: p?.start ?? "",
      end: p?.end ?? "",
      bankingFactor:
        p?.banking_factor_pct == null ? "" : `${p.banking_factor_pct}%`,
      quantum: p?.quantum_mw == null ? "" : `${p.quantum_mw}`,
      maxQuantum: p?.quantum_is_max ? "max" : "exact",
      fromTime: pickTime(p?.from_time, p?.fromTime, p?.start_time, p?.startTime),
      toTime: pickTime(p?.to_time, p?.toTime, p?.end_time, p?.endTime),
      basis: normalizeBasis(p?.basis),
      basisRaw: p?.basis_raw ?? "",
    }));

    setForm({
      loaNumber: src.loa_number ?? "",
      loaDate: src.loa_date ?? "",
      supplier: src.supplier ?? "",
      receiver: src.receiver ?? "",
      notes: src.notes ?? "",
      traderName: src.traderName ?? src.trader ?? "",
      supplyPeriods: mappedSupply.length ? mappedSupply : [{ ...EMPTY_SUPPLY }],
      returnPeriods: mappedReturn.length ? mappedReturn : [{ ...EMPTY_RETURN }],
    });
  };

  const handlePickFile = () => {
    if (uploading) return;
    fileInputRef.current?.click();
  };

  const extractAndContinue = async (file: File) => {
    cancelPollRef.current = false;
    setUploading(true);
    setUploadError(null);
    try {
      const postRes = await extractBankingContractPdf(file);
      const taskId = postRes?.task_id ?? postRes?.data?.task_id;
      if (!taskId) throw new Error("No task ID returned from server.");

      const MAX_ATTEMPTS = 60;
      for (let attempt = 0; attempt < MAX_ATTEMPTS; attempt++) {
        await new Promise<void>((resolve) => setTimeout(resolve, 2500));
        if (cancelPollRef.current) return;

        const statusRes = await getBankingExtractStatus(taskId);

        if (cancelPollRef.current) return;

        if (statusRes?.success === false || statusRes?.status === "failed") {
          throw new Error(
            statusRes?.data?.error ?? statusRes?.message ?? "Extraction failed.",
          );
        }

        if (statusRes?.status === "ready") {
          applyExtracted(statusRes);
          setStep("manual");
          return;
        }
      }
      throw new Error("Extraction timed out. Please try again.");
    } catch (err: any) {
      if (cancelPollRef.current) return;
      setUploadError(
        typeof err === "string" ? err : err?.message || "Failed to extract PDF",
      );
    } finally {
      if (!cancelPollRef.current) setUploading(false);
    }
  };

  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const f = e.target.files?.[0];
    e.target.value = "";
    if (!f) return;
    setPdfFile(f);
    setPdfMeta(null);
    setUploadError(null);
    void extractAndContinue(f);
  };

  return (
    <>
      {saveError && (
        <div className="fixed inset-0 z-[99999] flex items-center justify-center px-4 bg-black/20 backdrop-blur-[2px]">
          <div className="bg-white rounded-2xl shadow-[0_20px_60px_-10px_rgba(0,0,0,0.18)] w-full max-w-[380px] overflow-hidden animate-in zoom-in-95 fade-in duration-200">
            <div className=" w-full bg-gradient-to-r from-[#E05252] to-[#C0392B]" />

            <div className="flex flex-col items-center px-6 pt-7 pb-2 gap-3 relative">
              <button
                type="button"
                onClick={() => setSaveError(null)}
                className="absolute top-4 right-4 w-7 h-7 flex items-center justify-center rounded-lg text-[#B0AEC0] hover:text-[#6F6D7C] hover:bg-[#F5F6F8] cursor-pointer bg-transparent border-none"
              >
                <X size={15} />
              </button>
              <div className="w-14 h-14 rounded-full bg-[#FEF2F2] flex items-center justify-center">
                <AlertCircle size={28} className="text-[#C8311C]" />
              </div>
              <div className="flex flex-col items-center gap-1 text-center">
                <span className="text-[15px] font-bold text-[#C8311C]">
                  Failed
                </span>
                <span className="text-[11px] text-[#A8A6B2]">
                  Contract could not be created
                </span>
              </div>
            </div>

            <div className="mx-6 mb-0 px-3 py-3 bg-[#FEF2F2] rounded-xl border border-[#FECACA] text-center">
              <p className="text-[12px] text-[#7B3A35] leading-relaxed break-words">
                {saveError}
              </p>
            </div>

            <div className="px-6 py-2 flex justify-center">
              <button
                type="button"
                onClick={() => setSaveError(null)}
                className="px-8 py-2 rounded-lg bg-[#F1935C] text-white text-[12px] font-semibold hover:bg-[#F1935C] active:bg-[#F1935C] cursor-pointer border-none transition-colors"
              >
                OK, got it
              </button>
            </div>
          </div>
        </div>
      )}
      <div className="fixed inset-0 z-[9999] flex items-center justify-center bg-black/40 px-4 py-6">
        <div
          className={`bg-white rounded-xl shadow-xl w-full overflow-hidden flex flex-col max-h-[92vh] ${
            step === "manual"
              ? "max-w-[1300px]"
              : step === "choose"
                ? "max-w-[640px]"
                : "max-w-[460px]"
          }`}
        >
          <div className="flex items-center justify-between px-4 py-2 border-b border-[#E9E9F0] shrink-0">
            <div>
              <div className="text-[12px] font-bold tracking-[0.12em] text-[#A6A9B6] uppercase">
                {step === "manual"
                  ? "Manual Entry"
                  : step === "upload"
                    ? "New Contract"
                    : "New Contract"}
              </div>
              <div className="text-[18px] font-semibold text-[#454450]">
                {step === "manual"
                  ? "Contract Details"
                  : step === "upload"
                    ? "Upload LoA"
                    : "Choose a method"}
              </div>
            </div>
            <button
              type="button"
              onClick={resetAndClose}
              className="text-[#8C8A96] hover:text-[#6F6D7C] bg-white border border-[#ECEEF3] rounded-md cursor-pointer w-8 h-8 flex items-center justify-center"
            >
              <X size={14} />
            </button>
          </div>

          {step === "choose" && (
            <>
              <div className="px-5 py-5 grid grid-cols-2 gap-4">
                <MethodCard
                  icon={<Pencil size={16} className="text-[#525062]" />}
                  title="Manual entry"
                  description="Type counterparties, dates, and quanta yourself."
                  cta="START"
                  onClick={() => setStep("manual")}
                />
                <MethodCard
                  icon={<FileText size={16} className="text-[#525062]" />}
                  title="Upload LoA"
                  description="Drop a PDF; we extract schedule and factor."
                  cta="UPLOAD"
                  onClick={() => setStep("upload")}
                />
              </div>
              <div className="flex items-center justify-end gap-2 px-5 py-3 border-t border-[#ECEEF3] bg-[#FAFBFD]">
                <button
                  type="button"
                  onClick={resetAndClose}
                  className="border border-[#A9A8B1] bg-white rounded-lg px-4 py-1.5 text-xs text-[#7F818D] font-medium hover:bg-[#F5F6F8] cursor-pointer"
                >
                  Cancel
                </button>
              </div>
            </>
          )}

          {step === "manual" && (
            <form
              onSubmit={handleSubmit}
              className="flex flex-col flex-1 min-h-0"
            >
              <div className="px-5 py-4 flex flex-col gap-4 overflow-y-auto flex-1 min-h-0">
                <div className="grid grid-cols-5 gap-3">
                  <Field label="LOA Number" className="col-span-2">
                    <input
                      value={form.loaNumber}
                      onChange={(e) => setField("loaNumber", e.target.value)}
                      placeholder="E.g. 252603032"
                      className={inputClass}
                    />
                  </Field>
                  <Field label="LOA Date">
                    <input
                      type="date"
                      value={form.loaDate}
                      onChange={(e) => setField("loaDate", e.target.value)}
                      placeholder="dd-mm-yy"
                      className={inputClass}
                    />
                  </Field>
                  <Field label="Supplier">
                    <input
                      value={form.supplier}
                      onChange={(e) => setField("supplier", e.target.value)}
                      placeholder="E.g. MPPMCL"
                      className={inputClass}
                      required
                    />
                  </Field>
                  <Field label="Receiver">
                    <input
                      value={form.receiver}
                      onChange={(e) => setField("receiver", e.target.value)}
                      placeholder="E.g. CSPDCL"
                      className={inputClass}
                      required
                    />
                  </Field>
                </div>

                <div className="grid grid-cols-2 gap-4">
                  <Field label="Notes">
                    <input
                      value={form.notes}
                      onChange={(e) => setField("notes", e.target.value)}
                      placeholder="Special Conditions…"
                      className={inputClass}
                    />
                  </Field>
                  <Field label="Trader Name">
                    <TraderSelect
                      multiple
                      value={form.traderName}
                      onChange={(v) => {
                        if (traderMissing && v.trim()) setTraderMissing(false);
                        setField("traderName", v);
                      }}
                      placeholder="Select traders"
                      error={traderMissing}
                      triggerClassName={inputClass}
                    />
                  </Field>
                </div>

                <SectionHeader
                  title="Supply Periods"
                  accent
                  onAdd={addSupply}
                />
                <div className="flex flex-col gap-3">
                  {form.supplyPeriods.map((p, idx) => (
                    <PeriodCard
                      key={`supply-${idx}`}
                      removable={form.supplyPeriods.length > 1}
                      onRemove={() => removeSupply(idx)}
                    >
                      <div className="grid grid-cols-6 gap-3">
                        <Field label="Start Date">
                          <input
                            type="date"
                            value={p.start}
                            onChange={(e) =>
                              updateSupply(idx, "start", e.target.value)
                            }
                            placeholder="dd-mm-yy"
                            className={inputClass}
                          />
                        </Field>
                        <Field label="End Date">
                          <input
                            type="date"
                            value={p.end}
                            onChange={(e) =>
                              updateSupply(idx, "end", e.target.value)
                            }
                            placeholder="dd-mm-yy"
                            className={inputClass}
                          />
                        </Field>
                        <Field label="Quantum">
                          <input
                            value={p.quantum}
                            onChange={(e) =>
                              updateSupply(idx, "quantum", e.target.value)
                            }
                            placeholder="E.g. 300 Mw"
                            className={inputClass}
                          />
                        </Field>
                        <Field label="Max Quantum">
                          <select
                            value={p.maxQuantum}
                            onChange={(e) =>
                              updateSupply(
                                idx,
                                "maxQuantum",
                                e.target.value as MaxQuantumMode,
                              )
                            }
                            className={inputClass}
                          >
                            <option value="exact">Exact</option>
                            <option value="max">Up to (max)</option>
                          </select>
                        </Field>
                        <Field label="Start Time">
                          <input
                            type="time"
                            value={p.fromTime}
                            onChange={(e) =>
                              updateSupply(idx, "fromTime", e.target.value)
                            }
                            placeholder="hh:mm"
                            className={inputClass}
                          />
                        </Field>
                        <Field label="End Time">
                          <input
                            type="time"
                            value={p.toTime}
                            onChange={(e) =>
                              updateSupply(idx, "toTime", e.target.value)
                            }
                            placeholder="hh:mm"
                            className={inputClass}
                          />
                        </Field>
                      </div>
                      <div className="grid grid-cols-[1fr_2fr] gap-3 mt-3">
                        <Field label="Basis">
                          <select
                            value={p.basis}
                            onChange={(e) =>
                              updateSupply(
                                idx,
                                "basis",
                                e.target.value as BasisMode,
                              )
                            }
                            className={inputClass}
                          >
                            <option value="asAndWhen">As & When</option>
                            <option value="firm">Firm</option>
                          </select>
                        </Field>
                        <Field label="Basis Text Raw (Raw)">
                          <input
                            value={p.basisRaw}
                            onChange={(e) =>
                              updateSupply(idx, "basisRaw", e.target.value)
                            }
                            placeholder="Special Conditions…"
                            className={inputClass}
                          />
                        </Field>
                      </div>
                    </PeriodCard>
                  ))}
                </div>

                <SectionHeader title="Return Period" onAdd={addReturn} />
                <div className="flex flex-col gap-3">
                  {form.returnPeriods.map((p, idx) => (
                    <PeriodCard
                      key={`return-${idx}`}
                      removable={form.returnPeriods.length > 1}
                      onRemove={() => removeReturn(idx)}
                    >
                      <div className="grid grid-cols-7 gap-3">
                        <Field label="Start Date">
                          <input
                            type="date"
                            value={p.start}
                            onChange={(e) =>
                              updateReturn(idx, "start", e.target.value)
                            }
                            placeholder="dd-mm-yy"
                            className={inputClass}
                          />
                        </Field>
                        <Field label="End Date">
                          <input
                            type="date"
                            value={p.end}
                            onChange={(e) =>
                              updateReturn(idx, "end", e.target.value)
                            }
                            placeholder="dd-mm-yy"
                            className={inputClass}
                          />
                        </Field>
                        <Field label="Banking Factor">
                          <input
                            value={p.bankingFactor}
                            onChange={(e) =>
                              updateReturn(idx, "bankingFactor", e.target.value)
                            }
                            placeholder="E.g. 105%"
                            className={inputClass}
                          />
                        </Field>
                        <Field label="Quantum">
                          <input
                            value={p.quantum}
                            onChange={(e) =>
                              updateReturn(idx, "quantum", e.target.value)
                            }
                            placeholder="Optional"
                            className={inputClass}
                          />
                        </Field>
                        <Field label="Max Quantum">
                          <select
                            value={p.maxQuantum}
                            onChange={(e) =>
                              updateReturn(
                                idx,
                                "maxQuantum",
                                e.target.value as MaxQuantumMode,
                              )
                            }
                            className={inputClass}
                          >
                            <option value="exact">Exact</option>
                            <option value="max">Up to (max)</option>
                          </select>
                        </Field>
                        <Field label="Start Time">
                          <input
                            type="time"
                            value={p.fromTime}
                            onChange={(e) =>
                              updateReturn(idx, "fromTime", e.target.value)
                            }
                            placeholder="hh:mm"
                            className={inputClass}
                          />
                        </Field>
                        <Field label="End Time">
                          <input
                            type="time"
                            value={p.toTime}
                            onChange={(e) =>
                              updateReturn(idx, "toTime", e.target.value)
                            }
                            placeholder="hh:mm"
                            className={inputClass}
                          />
                        </Field>
                      </div>
                      <div className="grid grid-cols-[1fr_2fr] gap-3 mt-3">
                        <Field label="Basis">
                          <select
                            value={p.basis}
                            onChange={(e) =>
                              updateReturn(
                                idx,
                                "basis",
                                e.target.value as BasisMode,
                              )
                            }
                            className={inputClass}
                          >
                            <option value="asAndWhen">As & When</option>
                            <option value="firm">Firm</option>
                          </select>
                        </Field>
                        <Field label="Basis Text Raw (Raw)">
                          <input
                            value={p.basisRaw}
                            onChange={(e) =>
                              updateReturn(idx, "basisRaw", e.target.value)
                            }
                            placeholder="Special Conditions…"
                            className={inputClass}
                          />
                        </Field>
                      </div>
                    </PeriodCard>
                  ))}
                </div>
              </div>

              <div className="flex items-center justify-end gap-3 px-5 py-3 border-t border-[#ECEEF3] bg-white shrink-0">
                <div className="flex items-center gap-2 shrink-0">
                  <button
                    type="button"
                    onClick={resetAndClose}
                    disabled={saving}
                    className="border border-[#ECEEF3] bg-white rounded-md px-4 py-1.5 text-xs text-[#6B6877] font-medium hover:bg-[#FAFBFD] cursor-pointer disabled:opacity-60 disabled:cursor-not-allowed"
                  >
                    Cancel
                  </button>
                  <button
                    type="submit"
                    disabled={saving}
                    className="inline-flex items-center gap-1.5 border border-[#F1935C] bg-[#F1935C] rounded-md px-4 py-1.5 text-xs text-white font-semibold hover:bg-[#E07B43] hover:border-[#E07B43] cursor-pointer disabled:opacity-60 disabled:cursor-not-allowed"
                  >
                    {saving && <Loader2 size={12} className="animate-spin" />}
                    {saving ? "Saving…" : "Save Contract"}
                  </button>
                </div>
              </div>
            </form>
          )}

          {step === "upload" && (
            <div className="px-5 py-4 flex flex-col gap-3">
              <input
                ref={fileInputRef}
                type="file"
                accept="application/pdf,.pdf"
                className="hidden"
                onChange={handleFileChange}
              />
              <button
                type="button"
                onClick={handlePickFile}
                disabled={uploading}
                className={`relative border border-dashed rounded-lg px-4 py-6 flex flex-col items-center justify-center gap-2 text-center cursor-pointer disabled:cursor-wait ${
                  uploading
                    ? "border-[#F1935C] bg-[#FFF7F0]"
                    : "border-[#D6D8E0] bg-[#FAFBFD] hover:border-[#F1935C] hover:bg-[#FFF7F0]"
                }`}
              >
                {/* <div className="w-9 h-9 rounded-md bg-[#F0F1F5] flex items-center justify-center">
                {uploading ? (
                  <Loader2 size={16} className="text-[#F1935C] animate-spin" />
                ) : (
                  <Upload size={16} className="text-[#525062]" />
                )}
              </div> */}
                <div className="text-[13px] font-semibold text-[#6F6D7C] max-w-full break-words [overflow-wrap:anywhere]">
                  {uploading
                    ? `Extracting ${pdfFile?.name ?? "PDF"}…`
                    : pdfFile
                      ? pdfFile.name
                      : "Click to upload LoA PDF"}
                </div>
                <div className="text-[11px] text-[#8C8A96]">
                  {uploading
                    ? "Sending file to server — please wait."
                    : "We will read schedule and factor, then open the manual form."}
                </div>
                {uploading && (
                  <div className="mt-2 h-1 w-32 rounded-full bg-[#F1E0CE] overflow-hidden">
                    <div className="h-full w-1/3 bg-[#F1935C] animate-[pdfLoaderBar_1.1s_ease-in-out_infinite]" />
                  </div>
                )}
              </button>
              <style>{`@keyframes pdfLoaderBar{0%{transform:translateX(-100%)}100%{transform:translateX(300%)}}`}</style>

              {uploadError && (
                <div className="text-[11px] text-[#C0392B] bg-[#FDECEA] border border-[#F5C6C0] rounded-md px-2.5 py-1.5 flex items-center justify-between gap-2">
                  <span>{uploadError}</span>
                  {pdfFile && (
                    <button
                      type="button"
                      onClick={() =>
                        pdfFile && void extractAndContinue(pdfFile)
                      }
                      className="bg-transparent border-none text-[11px] text-[#C0392B] underline cursor-pointer p-0"
                    >
                      Retry
                    </button>
                  )}
                </div>
              )}

              <div className="flex items-center justify-between gap-2 pt-2 border-t border-[#ECEEF3] mt-1">
                <button
                  type="button"
                  disabled={uploading}
                  onClick={() => {
                    setStep("choose");
                    setPdfFile(null);
                    setPdfMeta(null);
                    setUploadError(null);
                  }}
                  className="bg-transparent border-none text-[12px] text-[#7F818D] hover:text-[#6F6D7C] cursor-pointer p-0 disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  ← Back
                </button>
                <div className="flex items-center gap-2">
                  <button
                    type="button"
                    onClick={resetAndClose}
                    className="border border-[#A9A8B1] bg-white rounded-md px-3 py-1.5 text-xs text-[#7F818D] font-medium hover:bg-[#FAFBFD] cursor-pointer"
                  >
                    Cancel
                  </button>
                  <button
                    type="button"
                    onClick={() => setStep("manual")}
                    className="border border-[#A9A8B1] bg-white rounded-md px-3 py-1.5 text-xs text-[#7F818D] font-medium hover:bg-[#FAFBFD] cursor-pointer"
                  >
                    Skip & enter manually
                  </button>
                </div>
              </div>
            </div>
          )}
        </div>
      </div>
    </>
  );
}

const inputClass =
  "w-full border border-[#E2E2E5] bg-white rounded-lg h-9 text-xs px-2.5 outline-none text-[#6F6D7C] placeholder:text-[#A8A6B2] focus:border-[#F1935C]";

function SectionHeader({
  title,
  accent = false,
  onAdd,
}: {
  title: string;
  accent?: boolean;
  onAdd: () => void;
}) {
  return (
    <div className="flex items-center justify-between mt-1">
      <span
        className={`text-[13px] font-medium ${
          accent ? "text-[#F1935C]" : "text-[#515E72]"
        }`}
      >
        {title}
      </span>
      <button
        type="button"
        onClick={onAdd}
        className="inline-flex text-[10px] items-center gap-1 border border-[#E8EAEF] bg-[#F1F5F9] rounded-lg px-3 py-1.5 text-[#515E72] font-medium hover:bg-[#F5F6F8] cursor-pointer"
      >
        <Plus size={12} /> Add Period
      </button>
    </div>
  );
}

function PeriodCard({
  removable,
  onRemove,
  children,
}: {
  removable: boolean;
  onRemove: () => void;
  children: React.ReactNode;
}) {
  return (
    <div
      className={`relative border border-[#E9E9F0] bg-[#F1F5F933] rounded-lg px-3 py-3 ${
        removable ? "pt-8" : ""
      }`}
    >
      {removable && (
        <button
          type="button"
          onClick={onRemove}
          className="absolute top-2 right-2 z-10 text-[#8C8A96] hover:text-[red] bg-white border border-[#ECEEF3] rounded-md cursor-pointer w-6 h-6 flex items-center justify-center"
        >
          <X size={12} />
        </button>
      )}
      {children}
    </div>
  );
}

function MethodCard({
  icon,
  title,
  description,
  cta,
  onClick,
}: {
  icon: React.ReactNode;
  title: string;
  description: string;
  cta: string;
  onClick: () => void;
}) {
  return (
    <button
      type="button"
      onClick={onClick}
      className="text-left bg-white border border-[#E9E9F0] rounded-lg p-4 flex flex-col gap-3 hover:border-[#F1935C] hover:shadow-sm transition cursor-pointer"
    >
      <div className="w-8 h-8 rounded-md bg-[#F5F6F8] flex items-center justify-center">
        {icon}
      </div>
      <div className="flex flex-col gap-1">
        <div className="text-[17px] font-bold text-[#6F6D7C]">{title}</div>
        <div className="text-[15px] text-[#ABAEBA] leading-snug">
          {description}
        </div>
      </div>
      <div className="inline-flex items-center gap-1 text-[11px] font-semibold tracking-[0.08em] text-[#8C8A96] uppercase mt-1">
        {cta} <ArrowRight size={12} />
      </div>
    </button>
  );
}

function Field({
  label,
  children,
  className,
}: {
  label: string;
  children: React.ReactNode;
  className?: string;
}) {
  return (
    <label className={`flex flex-col gap-1 min-w-0${className ? ` ${className}` : ""}`}>
      <span className="text-[12px] font-medium text-[#6F6D7C] tracking-wide truncate">
        {label}
      </span>
      {children}
    </label>
  );
}

export default AddContractModal;
